import React, { useState, useEffect, useRef } from 'react';
import { Ship, FileText, Edit, FileDown } from 'lucide-react';
import { getCloudFrontUrl } from '../../utils/s3Upload';

const SeaExportTableReadOnly = ({ shipments, loading = false, onEdit = null, onGenerateInvoice = null, onEditInvoice = null, invoiceMap = {}, userRole = null, highlightedBl = null, flush = false }) => {
  const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);
  const topScrollContentRef = useRef(null);
  const tableRef = useRef(null);
  const isScrollingRef = useRef(false);
  const highlightRowRef = useRef(null);
  const splitPdfValues = (value) => {
    if (!value) return [];
    return String(value)
      .split(/\s*&\s*|\s*,\s*/)
      .map(v => v.trim())
      .filter(Boolean);
  };

  const splitContainerValues = (value) => {
    if (!value) return [];
    const isValid = (v) => {
      const t = String(v || '').trim();
      return t !== '' && t !== '-';
    };
    if (Array.isArray(value)) return value.map(v => String(v || '').trim()).filter(isValid);
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed || trimmed === '-' || trimmed === '[]' || trimmed === '[""]') return [];
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map(v => String(v || '').trim()).filter(isValid);
        }
      } catch {
        // continue with delimiter parsing
      }
    }
    return String(value)
      .split(/\s*&\s*|\s*,\s*/)
      .map(v => v.trim())
      .filter(isValid);
  };

  const formatContainerNo = (value) => {
    const values = splitContainerValues(value);
    return values.length > 0 ? values.join(' & ') : '';
  };
  const normalizedRole = String(userRole || '').toLowerCase();
  const isSuperAdmin = normalizedRole === 'super_admin' || normalizedRole === 'superadmin';
  const showActions = userRole === 'accountant' && onEdit;
  const totalColumns = 26 + (isSuperAdmin ? 2 : 0) + (showActions ? 1 : 0);

  // Scroll to highlighted row
  useEffect(() => {
    if (highlightedBl && highlightRowRef.current) {
      highlightRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedBl]);

  // Sync table width with scrollbars
  useEffect(() => {
    const updateScrollWidth = () => {
      if (tableRef.current && topScrollContentRef.current) {
        const tableWidth = tableRef.current.scrollWidth;
        topScrollContentRef.current.style.width = `${tableWidth}px`;
      }
    };

    updateScrollWidth();
    window.addEventListener('resize', updateScrollWidth);
    const observer = new MutationObserver(updateScrollWidth);
    if (tableRef.current) {
      observer.observe(tableRef.current, { childList: true, subtree: true });
    }

    return () => {
      window.removeEventListener('resize', updateScrollWidth);
      observer.disconnect();
    };
  }, [shipments]);

  // Dual scrollbar sync
  useEffect(() => {
    const topScroll = topScrollRef.current;
    const bottomScroll = bottomScrollRef.current;
    if (!topScroll || !bottomScroll) return;

    const handleTopScroll = () => {
      if (!isScrollingRef.current) {
        isScrollingRef.current = true;
        bottomScroll.scrollLeft = topScroll.scrollLeft;
        requestAnimationFrame(() => {
          isScrollingRef.current = false;
        });
      }
    };

    const handleBottomScroll = () => {
      if (!isScrollingRef.current) {
        isScrollingRef.current = true;
        topScroll.scrollLeft = bottomScroll.scrollLeft;
        requestAnimationFrame(() => {
          isScrollingRef.current = false;
        });
      }
    };

    topScroll.addEventListener('scroll', handleTopScroll);
    bottomScroll.addEventListener('scroll', handleBottomScroll);

    return () => {
      topScroll.removeEventListener('scroll', handleTopScroll);
      bottomScroll.removeEventListener('scroll', handleBottomScroll);
    };
  }, []);

  if (!shipments) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
        <Ship size={48} className="mx-auto text-slate-400 dark:text-slate-600 mb-4" />
        <p className="text-slate-500 dark:text-slate-400">No shipments data available</p>
      </div>
    );
  }

  return (
    /* `flush` means a console panel already draws the card around us. */
    <div className={`overflow-hidden bg-white dark:bg-[#0e1626] ${flush ? '' : 'rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm'}`}>
      {/* Top Scrollbar */}
      <div 
        ref={topScrollRef}
        className="overflow-x-auto overflow-y-hidden h-4 bg-slate-100 dark:bg-slate-800/60"
        style={{ scrollbarWidth: 'thin', scrollbarColor: '#06b6d4 #e5e7eb' }}
      >
        <div ref={topScrollContentRef} className="h-1"></div>
      </div>

      {/* Table Container */}
      <div
        ref={bottomScrollRef}
        className="overflow-x-auto"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#06b6d4 #e5e7eb'
        }}
      >
        <table ref={tableRef} className="min-w-full">
          <thead className="bg-slate-50 dark:bg-[#111b2e] sticky top-0 z-10 border-b border-slate-200 dark:border-slate-800">
            <tr>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">S.No</th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">Port Code</th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">SB No</th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">SB Date</th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">PKG</th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">GWT <span className="normal-case font-medium text-cyan-200">(kg)</span></th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">Port of Loading</th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">State of Origin</th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">Port Discharge Code</th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">Port of Discharge</th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">Country Destination</th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">Exporter Name</th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">Buyer Name</th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">Mode</th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">FOB Value <span className="normal-case font-medium text-cyan-200">(₹)</span></th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">DBK Claim <span className="normal-case font-medium text-cyan-200">(₹)</span></th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">IGST Amt <span className="normal-case font-medium text-cyan-200">(₹)</span></th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">Rodtep Amt <span className="normal-case font-medium text-cyan-200">(₹)</span></th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">B/L Number</th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">Container No</th>
              <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">Container Qty</th>
              <th className="px-4 py-3 text-center text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">B/L PDF</th>
              <th className="px-4 py-3 text-center text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">LEO PDF</th>
              <th className="px-4 py-3 text-center text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">INV/PL PDF</th>
              <th className="px-4 py-3 text-center text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">INVOICE PDF</th>
              <th className="px-4 py-3 text-center text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">PUR PDF</th>
              {isSuperAdmin && (
                <>
                  <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">Admin Name</th>
                  <th className="px-4 py-3 text-left text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">Last Edited By</th>
                </>
              )}
              {showActions && (
                <th className="px-4 py-3 text-center text-[10.5px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.1em] whitespace-nowrap">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
            {loading ? (
              <tr>
                <td colSpan={totalColumns} className="px-4 py-8 text-center">
                  <div className="flex flex-col items-center gap-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
                    <p className="text-slate-600 dark:text-slate-400 font-medium">Loading sea export shipments...</p>
                  </div>
                </td>
              </tr>
            ) : shipments.length === 0 ? (
              <tr>
                <td colSpan={totalColumns} className="px-4 py-8 text-center">
                  <Ship size={48} className="mx-auto text-slate-400 dark:text-slate-600 mb-4" />
                  <p className="text-slate-500 dark:text-slate-400 text-lg font-medium">No sea export shipments found</p>
                  <p className="text-slate-400 dark:text-slate-500 text-sm mt-2">Your sea export shipments will appear here</p>
                </td>
              </tr>
            ) : (
              shipments.map((shipment, index) => {
                const isHighlighted = highlightedBl && shipment.blNumber === highlightedBl;
                const invoiceHistory = invoiceMap[shipment.blNumber] || null;
                return (
                <tr
                  key={shipment.id}
                  ref={isHighlighted ? highlightRowRef : null}
                  className={`hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition ${
                    isHighlighted
                      ? 'bg-yellow-100 dark:bg-yellow-900/30 animate-pulse ring-2 ring-yellow-400 dark:ring-yellow-500'
                      : ''
                  }`}
                >
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{index + 1}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.portCode}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.sbNo}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.sbDate}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.pkg}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.gwt}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.portOfLoading}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.stateOfOrigin}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.portOfDischargeCode}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.portOfDischarge}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.countryOfFinalDestination}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.exporterName}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.buyerName}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.mode}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.fobValue}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.dbkClaim}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.igstAmt}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.rodtepAmt}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.blNumber}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{formatContainerNo(shipment.containerNo)}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">{shipment.containerQty}</td>
                  
                  {/* PDF Links */}
                  <td className="px-4 py-4 text-center whitespace-nowrap">
                    {shipment.blPdf ? (
                      <a
                        href={getCloudFrontUrl(shipment.blPdf)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition"
                      >
                        <FileText size={18} />
                        <span className="text-xs font-medium">View</span>
                      </a>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500 text-xs">-</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-center whitespace-nowrap">
                    {splitPdfValues(shipment.leoPdf).length > 0 ? (
                      <a
                        href={getCloudFrontUrl(splitPdfValues(shipment.leoPdf)[0])}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 transition"
                      >
                        <FileText size={18} />
                        <span className="text-xs font-medium">{splitPdfValues(shipment.leoPdf).length > 1 ? `View (${splitPdfValues(shipment.leoPdf).length})` : 'View'}</span>
                      </a>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500 text-xs">-</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-center whitespace-nowrap">
                    {shipment.invPlPdf ? (
                      <a
                        href={getCloudFrontUrl(shipment.invPlPdf)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 transition"
                      >
                        <FileText size={18} />
                        <span className="text-xs font-medium">View</span>
                      </a>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500 text-xs">-</span>
                    )}
                  </td>                  <td className="px-4 py-4 text-center whitespace-nowrap">
                    {shipment.invoicePdf ? (
                      <a
                        href={getCloudFrontUrl(shipment.invoicePdf)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-300 transition"
                      >
                        <FileText size={18} />
                        <span className="text-xs font-medium">View</span>
                      </a>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500 text-xs">-</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-center whitespace-nowrap">
                    {shipment.purPdf ? (
                      <a
                        href={getCloudFrontUrl(shipment.purPdf)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-teal-600 hover:text-teal-800 dark:text-teal-400 dark:hover:text-teal-300 transition"
                      >
                        <FileText size={18} />
                        <span className="text-xs font-medium">View</span>
                      </a>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500 text-xs">-</span>
                    )}
                  </td>
                  
                  {/* Admin columns - superadmin only */}
                  {isSuperAdmin && (
                    <>
                      <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                        {shipment.admin_name || shipment.adminName || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-400">
                        {shipment.edited_by || shipment.editedBy || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </td>
                    </>
                  )}
                  
                  {/* Actions column for accountants */}
                  {showActions && (
                    <td className="px-4 py-4 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2">
                        {invoiceHistory ? (
                          <button
                            onClick={() => onEditInvoice && onEditInvoice(shipment, invoiceHistory)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition font-medium text-xs"
                            title="Revise Invoice"
                          >
                            <FileDown size={16} />
                            <span>Revise</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => onGenerateInvoice && onGenerateInvoice(shipment)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition font-medium text-xs"
                            title="Generate Invoice"
                          >
                            <FileDown size={16} />
                            <span>Generate</span>
                          </button>
                        )}
                        <button
                          onClick={() => onEdit(shipment)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-lg transition font-medium text-xs"
                          title="Upload PDFs"
                        >
                          <Edit size={16} />
                          <span>Upload</span>
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SeaExportTableReadOnly;
